import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {SalesLeagueLeaderboardResponse, SalesLeaguePeriod} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeagueBoardHeader} from '../../../components/SalesLeagueBoardHeader';
import {SalesLeagueBoardLayout} from '../../../components/SalesLeagueBoardLayout';
import {SalesLeagueBoardSkeleton} from '../../../components/SalesLeagueBoardSkeleton';
import {SALES_LEAGUE_PERIOD_LABEL} from '../../../constants/SALES_LEAGUE_PERIODS';
import {SalesLeagueBoardKpis} from './SalesLeagueBoardKpis';
import {SalesLeagueBoardPodium} from './SalesLeagueBoardPodium';
import {SalesLeagueEmptyBoard} from './SalesLeagueEmptyBoard';
import {SalesLeagueTable} from './SalesLeagueTable';

interface SalesLeagueBoardProps extends RequiredTestIdProps {
  /** Nullish until the query resolves; the board skeletons its contents meanwhile. */
  leaderboard: SalesLeagueLeaderboardResponse | Nullish;
  isLoading: boolean;
  period: SalesLeaguePeriod;
  onPeriodChange: (period: SalesLeaguePeriod) => void;
  onExport: () => void;
  isExporting?: boolean;
}

/**
 * Owns three states below one header: loading, nothing sold, and a board. The header is
 * outside all of them because the period switcher has to stay reachable throughout — the
 * way out of both an empty period and a slow one.
 */
export function SalesLeagueBoard(props: SalesLeagueBoardProps) {
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const leaderboard = props.leaderboard;
  const isLoading = props.isLoading || isNil(leaderboard);

  // No compact range formatter exists in platform/locale, and collapsing a shared month
  // would mean string surgery on locale-formatted output. Both endpoints are formatted
  // in full and the separator is left to translators.
  const periodRangeLabel = isNotNil(leaderboard)
    ? i18n.t('page.salesLeague.periodRange', {
        from: formatDateTime('dateShort', parseDate(leaderboard.period.from)),
        to: formatDateTime('dateShort', parseDate(leaderboard.period.to)),
      })
    : '';

  const periodLabel = i18n.t(SALES_LEAGUE_PERIOD_LABEL[props.period]);

  // `salesReps` counts the reps who sold in this period — the same cohort as the rows,
  // never the branch headcount.
  const subtitle = isNotNil(leaderboard)
    ? i18n.t('page.salesLeague.header.subtitleRep', {
        range: periodRangeLabel,
        count: formatNumber(leaderboard.totals.salesReps) ?? EMPTY_PLACEHOLDER,
      })
    : '';

  // The branch is only known once the board arrives, so the heading falls back to the
  // module's own name rather than showing a half-built title.
  const heading = isNotNil(leaderboard)
    ? i18n.t('page.salesLeague.heading', {branch: leaderboard.branch.name})
    : i18n.t('module.salesLeague.repLeaderboard');

  const header = (
    <SalesLeagueBoardHeader
      heading={heading}
      subtitle={subtitle}
      isLoading={isLoading}
      period={props.period}
      onPeriodChange={props.onPeriodChange}
      onExport={props.onExport}
      isExporting={props.isExporting}
      data-testid={suffixTestId('header', props)}
    />
  );

  // Returning early keeps the remaining branch down to two cases and narrows `leaderboard`
  // for everything below it.
  if (isLoading || isNil(leaderboard)) {
    return (
      <SalesLeagueBoardLayout header={header}>
        <SalesLeagueBoardSkeleton />
      </SalesLeagueBoardLayout>
    );
  }

  return (
    <SalesLeagueBoardLayout header={header}>
      {isEmpty(leaderboard.rows) ? (
        <SalesLeagueEmptyBoard data-testid={suffixTestId('board', props)} />
      ) : (
        <>
          <SalesLeagueBoardPodium
            leaderboard={leaderboard}
            periodLabel={periodLabel}
            data-testid={suffixTestId('podium', props)}
          />
          <SalesLeagueBoardKpis
            leaderboard={leaderboard}
            periodLabel={periodLabel}
            data-testid={suffixTestId('kpi', props)}
          />
          <SalesLeagueTable
            // Page and rows-per-page are local to the table; a period change is a
            // different board, so the state is reset rather than carried over.
            key={props.period}
            leaderboard={leaderboard}
            periodLabel={periodLabel}
            data-testid={suffixTestId('leaderboard', props)}
          />
        </>
      )}
    </SalesLeagueBoardLayout>
  );
}
